import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, makeStyles } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { Block, Heading, Tooltip } from '@klp/design';
import { LocalLoader } from '@klp/design/Loader';
import { Main } from '@klp/layout';
import { useGetProsjektAvfallQuery } from '../services/rtkApi';
import { ProsjektAvfallTable } from '../components/ProsjektAvfallTable';
import { ProsjektAvfallUpload } from '../components/ProsjektAvfallUpload';
import { ProsjektAvfallKlimagassUpload } from '../components/ProsjektAvfallKlimagassUpload';
import {
    formatDateAPI,
    getFirstDateOfCurrentQuarter,
    parseSafeDate,
} from '@forvalter/utils/dateTimeFormatter';
import { EnvironmentRoutes, localStorageReferences } from '../config';
import {
    clearStorage,
    getFromStorage,
    saveToStorage,
} from '@klp/utils/storage';
import { isValid } from 'date-fns';
import { RotateLeft } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
    firstDatePicker: {
        marginRight: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(2),
        },
    },
    buttonRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
}));

export default function ProsjektAvfallView() {
    const { t } = useTranslation();
    const classes = useStyles();
    const today = useMemo(() => new Date(), []);
    const firstDayOfLatestQuarter = useMemo(
        () => getFirstDateOfCurrentQuarter(today),
        [today],
    );
    const defaultFromDate =
        parseSafeDate(
            getFromStorage<string | undefined>(
                localStorageReferences.SELECTED_PROSJEKTAVFALL_FROM_DATE,
            ),
        ) ?? firstDayOfLatestQuarter;
    const defaultToDate =
        parseSafeDate(
            getFromStorage<string | undefined>(
                localStorageReferences.SELECTED_PROSJEKTAVFALL_TO_DATE,
            ),
        ) ?? today;
    const [selectedFromDate, setSelectedFromDate] = useState<Date | undefined>(
        defaultFromDate,
    );
    const [selectedToDate, setSelectedToDate] = useState<Date | undefined>(
        defaultToDate,
    );

    const {
        data: prosjektAvfall,
        isLoading,
        isFetching,
    } = useGetProsjektAvfallQuery({
        avsluttetFromDate: formatDateAPI(selectedFromDate),
        avsluttetToDate: formatDateAPI(selectedToDate),
    });

    const handleSelectedFromDate = (date: Date) => {
        if (!isValid(date)) return;

        saveToStorage(
            localStorageReferences.SELECTED_PROSJEKTAVFALL_FROM_DATE,
            date,
        );
        setSelectedFromDate(date);
    };

    const handleSelectedToDate = (date: Date) => {
        if (!isValid(date)) return;

        saveToStorage(
            localStorageReferences.SELECTED_PROSJEKTAVFALL_TO_DATE,
            date,
        );

        setSelectedToDate(date);
    };

    const selectedDatesIsLatestQuarter = useMemo(
        () =>
            selectedFromDate === firstDayOfLatestQuarter &&
            selectedToDate === today,
        [firstDayOfLatestQuarter, today, selectedFromDate, selectedToDate],
    );

    const handleResetDates = () => {
        clearStorage(localStorageReferences.SELECTED_PROSJEKTAVFALL_FROM_DATE);
        clearStorage(localStorageReferences.SELECTED_PROSJEKTAVFALL_TO_DATE);

        setSelectedFromDate(firstDayOfLatestQuarter);
        setSelectedToDate(today);
    };

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="avfall"
                    label={t('sustainability.prosjektAvfall.title')}
                    href={EnvironmentRoutes.prosjektAvfall()}
                />
            </Main.Breadcrumbs>
            <Block paddingBottom="fat">
                <Heading klpHeadingLevel="TWO" klpColor="dark">
                    {t('sustainability.prosjektAvfall.title')}
                </Heading>
                {t('sustainability.prosjektAvfall.description')}
                <div className={classes.buttonRow}>
                    <ProsjektAvfallUpload prosesser={prosjektAvfall ?? []} />
                    <ProsjektAvfallKlimagassUpload
                        prosesser={prosjektAvfall ?? []}
                    />
                </div>
            </Block>
            <Block paddingBottom="compact">
                <KeyboardDatePicker
                    className={classes.firstDatePicker}
                    allowKeyboardControl={true}
                    label={t('sustainability.prosjektAvfall.fromDate')}
                    variant="inline"
                    inputVariant="outlined"
                    InputAdornmentProps={{
                        position: 'end',
                    }}
                    value={selectedFromDate}
                    onChange={handleSelectedFromDate}
                    disableFuture={true}
                    format="dd-MM-yyyy"
                    autoOk={true}
                    maxDate={today}
                />
                <KeyboardDatePicker
                    allowKeyboardControl={true}
                    label={t('sustainability.prosjektAvfall.toDate')}
                    variant="inline"
                    inputVariant="outlined"
                    InputAdornmentProps={{
                        position: 'end',
                    }}
                    value={selectedToDate}
                    onChange={handleSelectedToDate}
                    format="dd-MM-yyyy"
                    autoOk={true}
                    maxDate={today}
                    minDate={selectedFromDate}
                />
                {!selectedDatesIsLatestQuarter && (
                    <Tooltip
                        translation="sustainability.prosjektAvfall.reset"
                        title=""
                    >
                        <IconButton onClick={handleResetDates}>
                            <RotateLeft />
                        </IconButton>
                    </Tooltip>
                )}
            </Block>
            <LocalLoader isLoading={isLoading || isFetching}>
                {prosjektAvfall && (
                    <ProsjektAvfallTable prosjektAvfall={prosjektAvfall} />
                )}
            </LocalLoader>
        </Main>
    );
}
